import { useMemo } from "react";

import { useValidBundles } from "@/hooks/useValidBundles";
import type { BadgeType } from "@/v2-components/global-navigation-bar/_const/navigation-menus";
import { STORE_LINEUP_LINKS } from "@/v2-components/global-navigation-bar/_const/store-panel";

/**
 * GNB Store 패널 「전체 라인업」의 **`Bundle Set` 링크에만** 붙는 배지
 * (시안 데스크탑 `29229:21435` + `30412:146514` · 모바일 `29303:21651` + `30412:146520`).
 *
 * ## 왜 정적 필드가 아니라 훅인가
 *
 * 시안이 `New`·`Sale` 을 **그려 뒀지만** 그건 오늘의 상태를 그린 것이고, 실제 신호는
 * admin 이 제품마다 붙이는 `product.tags`(`"new"`·`"sale"`)다. `STORE_LINEUP_LINKS` 에
 * `badge: "new"` 를 박으면 세일이 끝난 뒤에도 배지가 남는다 — 같은 이유로
 * `menu-item-badge.tsx` 가 *"라이브 커머스 신호"* 라고 적어 뒀다.
 *
 * ## ★집합은 `useValidBundles()` 를 통과한 것이어야 한다
 *
 * 배지는 *"저 링크를 누르면 새것/세일이 있다"* 는 약속이므로, 목적지
 * (`/tactsuit/bundles`)가 **실제로 보여주는 집합**에서 세야 한다. raw `useProducts()`
 * 로 세면 두 겹이 빠진다(`docs/bundle-region-policy.md`):
 *
 * ```
 *   getAllBundles    unlisted 번들 제외 (tactglove-dk2/dk3-bundle)
 *   getValidBundles  게임키 번들을 통화로 게이팅 — KRW 는 일반 게임 키 번들 제외(번들 전용 키 번들은 예외)
 * ```
 *
 * 실측(2026-09-07 prod `admin-apis /api/v1/shop-products`): API 가 주는 `BUNDLE` 은
 * 8건이지만 배지의 입력은 **US 6건 · KR 4건**이다.
 *
 * ```
 *   제외      tactglove-dk3-bundle ["new"]   ← unlisted. `new` 2건 중 하나가 여기 있다
 *             tactglove-dk2-bundle []
 *   KR 추가   metro-awakening ["sale"] · arizona ["sale"]   ← 게임키 · KRW
 *   남는 것   new 1건(Starter Bundle: Camo) · sale US 6 / KR 4
 * ```
 *
 * ⚠️ **오늘은 두 집합의 배지 결과가 같다**(어느 쪽으로 세도 `New + Sale`). 그래서 raw 로
 * 짜면 **우연히 맞고** 태그가 옮겨가는 날 조용히 틀린다 — 지금 `new` 는 **한 건**에만
 * 걸려 있어서 그 한 건이 unlisted 쪽으로 가면 바로 갈린다.
 *
 * ## 비용
 *
 * `useValidBundles()` 는 `useSellableGames()`(게임 카탈로그)를 끌고 온다. GNB 에 쿼리가
 * 하나 늘지만 패널은 `armed`(네비에 포인터·포커스가 들어온 뒤) 이후에 마운트되므로
 * **페이지 로드가 아니라 GNB 상호작용 시점**에 돈다.
 */

/**
 * ★**`Sale` 하나만 붙인다** (#5603 — 작성자 요청).
 *
 * 시안(`30412:146514`·`146520`)은 `New`+`Sale` 둘을 그렸지만, 요청은 *"번들세트에는 sale
 * 딱지만"* 이다. 시안이 그린 것은 **그날의 상태**고 이건 규칙이다.
 *
 * ⚠️ 오늘 지우는 것은 실재하는 배지다 — prod 실측(2026-09-08 `admin-apis
 * /api/v1/shop-products` 37건): 번들 중 `new` 는 `starter-bundle-camo` 한 건뿐이고,
 * 그 한 건 때문에 `New` 가 떠 있었다. `sale` 은 번들 6건에 걸려 있다.
 *
 * `BadgeType` 자체는 `new` 를 계속 갖는다 — 게임 메뉴(`Game Keys`)가 쓴다.
 */
const BADGE_ORDER: readonly BadgeType[] = ["sale"];

/**
 * 배지가 붙는 링크. 시안은 `Bundle Set` **한 행에만** `BadgeArea` 를 뒀다 — 형제
 * `link-액세서리`(38h)·`link-전체 제품 보기`(36h) 에는 없고, 그래서 `Bundle Set` 만
 * 데스크탑 38h · 모바일 40h 로 2px 높다(배지 20px > 텍스트 18px).
 */
export const BADGED_LINEUP_HREF: (typeof STORE_LINEUP_LINKS)[number]["href"] =
  "/tactsuit/bundles";

/**
 * 지금 판매 중인 번들의 `tags` 를 합쳐 `["new", "sale"]` 중 해당하는 것만 시안 순서로
 * 돌려준다. 해당 없으면 빈 배열이라 호출처가 `length` 로 가른다.
 *
 * `"best"` 등 다른 태그는 무시한다 — 시안의 `BadgeArea` 는 `New`·`Sale` 두 종류만 그리고
 * `BadgeType` 도 그 둘이다.
 */
export function useBundleSetBadges(): BadgeType[] {
  const validBundles = useValidBundles();

  return useMemo(() => {
    const tags = new Set(validBundles.flatMap((bundle) => bundle.tags ?? []));
    return BADGE_ORDER.filter((badge) => tags.has(badge));
  }, [validBundles]);
}
